import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { UiActions } from '@store/actions/ui.actions';
import { AccessService } from '@services/access.service';

export interface DeniedToastConfig {
  summary: string;
  messageKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConditionalGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly access = inject(AccessService);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    // Wait for roles and feature flags to be loaded before evaluating, otherwise a hard
    // reload (F5) onto a guarded route reads the empty initial state and wrongly denies.
    return this.access.whenReady().pipe(
      switchMap(() => this.evaluate(route)),
    );
  }

  private evaluate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const requiredRoles = route.data['requiredRoles'] as string[] | undefined;
    const featureKey = route.data['featureKey'] as string | undefined;
    const availabilityKey = route.data['availabilityKey'] as string | undefined;
    const deniedToast = route.data['deniedToast'] as DeniedToastConfig | undefined;

    if (!this.access.hasAnyRole(requiredRoles || [])) {
      return of(this.deny(deniedToast));
    }

    if (featureKey && !this.access.canAccessFeature(featureKey)) {
      return of(this.deny(deniedToast));
    }

    if (availabilityKey) {
      return this.access.checkAvailability(availabilityKey).pipe(
        map((available) => available ? true : this.deny(deniedToast)),
      );
    }

    return of(true);
  }

  private deny(toast?: DeniedToastConfig): UrlTree {
    if (toast) {
      this.store.dispatch(
        new UiActions.toasterShowError({
          summary: toast.summary,
          message: this.translate.instant(toast.messageKey),
        }),
      );
    }
    return this.router.createUrlTree(['/']);
  }
}
